import React, { useState, useEffect } from 'react';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Info, Phone, HelpCircle, Shield, RefreshCw, Save, 
  Edit, Eye, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../../components/LoadingSpinner';

const PAGE_CONFIGS = [
  { slug: 'about-us', title: 'About Us', icon: Info, color: 'blue' },
  { slug: 'contact', title: 'Contact Us', icon: Phone, color: 'green' },
  { slug: 'help-faq', title: 'Help & FAQ', icon: HelpCircle, color: 'purple' },
  { slug: 'privacy-policy', title: 'Privacy Policy', icon: Shield, color: 'orange' },
  { slug: 'terms-of-service', title: 'Terms of Service', icon: FileText, color: 'red' },
  { slug: 'refund-policy', title: 'Refund Policy', icon: RefreshCw, color: 'teal' }
];

function ContentEditor({ pageConfig, onSave }) {
  const [content, setContent] = useState({
    slug: pageConfig.slug,
    title: pageConfig.title,
    content: '',
    meta_title: '',
    meta_description: '',
    is_active: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchContent();
  }, [pageConfig.slug]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${API_URL}/api/content/${pageConfig.slug}`);
      if (response.data) {
        setContent({
          ...content,
          ...response.data,
          slug: pageConfig.slug
        });
      }
    } catch (error) {
      // Use default content when page config isn't found
      if (error?.response?.status && error.response.status !== 404) {
        console.error('Page config fetch failed:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axiosInstance.post(`${API_URL}/api/admin/content`, content);
      toast.success(`${pageConfig.title} saved successfully!`);
      if (onSave) onSave();
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const Icon = pageConfig.icon;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className={`w-5 h-5 text-${pageConfig.color}-600`} />
          {pageConfig.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Page Title</Label>
            <Input
              value={content.title}
              onChange={(e) => setContent({...content, title: e.target.value})}
              placeholder="Page title"
            />
          </div>
          <div className="space-y-2">
            <Label>Meta Title (SEO)</Label>
            <Input
              value={content.meta_title || ''}
              onChange={(e) => setContent({...content, meta_title: e.target.value})}
              placeholder="SEO meta title"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Meta Description (SEO)</Label>
          <Input
            value={content.meta_description || ''}
            onChange={(e) => setContent({...content, meta_description: e.target.value})}
            placeholder="SEO meta description"
          />
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label>Content (HTML supported)</Label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreview(!preview)}
            >
              {preview ? <Edit className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
              {preview ? 'Edit' : 'Preview'}
            </Button>
          </div>
          
          {preview ? (
            <div 
              className="min-h-[300px] p-4 border rounded-lg bg-white prose max-w-none"
              dangerouslySetInnerHTML={{ __html: content.content || '<p class="text-gray-400">No content yet</p>' }}
            />
          ) : (
            <textarea
              value={content.content || ''}
              onChange={(e) => setContent({...content, content: e.target.value})}
              placeholder="Enter HTML content here..."
              className="w-full min-h-[300px] p-4 border rounded-lg font-mono text-sm"
            />
          )}
        </div>

        <div className="flex items-center gap-4 pt-4">
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save {pageConfig.title}
          </Button>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={content.is_active}
              onChange={(e) => setContent({...content, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <span className="text-sm">Active</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminContent() {
  const [activeTab, setActiveTab] = useState('about-us');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Content Management</h1>
        <p className="text-gray-600 mt-1">Manage static pages like About Us, Contact, Legal pages</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 bg-white border p-1 rounded-xl h-auto">
          {PAGE_CONFIGS.map((page) => {
            const Icon = page.icon;
            return (
              <TabsTrigger 
                key={page.slug} 
                value={page.slug}
                className="flex items-center gap-1 px-3 py-2 data-[state=active]:bg-green-600 data-[state=active]:text-white"
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{page.title}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {PAGE_CONFIGS.map((page) => (
          <TabsContent key={page.slug} value={page.slug}>
            <ContentEditor pageConfig={page} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
